import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import type { MatchScoreBreakdown } from '../utils/data/calculate-job-match-score';
import type { SearchInferenceBullet } from '../types/job-match';
import { getMatchDonutScoreColor } from '../utils/match-donut/get-match-donut-score-color';
import { mapMatchScoreToTier } from '../utils/match-donut/mapMatchScoreToTier';
import { useDocumentDarkClass } from '../hooks/useDocumentDarkClass';
import { ChartPopoverPortal } from '../pages/job/@jobId/@seoSlug/components/ChartPopoverPortal';
import { Link } from './Link';
import { buildLocalizedProfileHref } from '../utils/routing/build-localized-profile-href';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';

/** Pre-rendered strings for MatchDonut when used without IntlProvider (e.g. static SRP). */
export interface MatchDonutStaticStrings {
  ariaLabelTemplate: string;
  breakdownTitle: string;
  breakdownSalary: string;
  breakdownVacation: string;
  breakdownBenefits: string;
  breakdownRemoteness: string;
  breakdownLanguages: string;
  /** Aria label for empty donut (no score / login to see). */
  loginPromptAria: string;
}

interface MatchDonutProps {
  /** 0-100 when known; null for static view when user not logged in / score not available. */
  score: number | null;
  size?: number;
  className?: string;
  breakdown?: MatchScoreBreakdown;
  /** Profile-based match vs inferred from recent searches (affects tooltip copy). */
  matchScoreSource?: 'profile' | 'recent-searches';
  /** Shown in the tooltip when {@link matchScoreSource} is `recent-searches`. */
  searchInferenceBullets?: SearchInferenceBullet[];
  /** When provided, no useIntl/FormattedMessage (for static SSR). Enables null score → empty donut with "?". */
  strings?: MatchDonutStaticStrings;
  /** V2 redesign: ring stroke uses the company's brand panel color instead of the score-based color. */
  ringColorOverride?: string;
  /**
   * V2 redesign: center score digit uses a fixed ink color instead of following the ring color.
   * A plain string is used as-is regardless of theme (rare — only for a color known to work on
   * both). The donut sits on the page's own background (light/dark), not necessarily the ring
   * color's panel, so a fixed ink color needs a `{ light, dark }` pair to stay readable in both.
   */
  labelColorOverride?: string | { light: string; dark: string };
  /**
   * When provided (editorial-rail redesign), clicking a filled-out donut calls this instead of
   * opening the breakdown popover — used to scroll to the full match-details section further
   * down the page instead of duplicating it in a tooltip. Has no effect on the empty/login-prompt
   * state (score === null), which always keeps its own popover since there's nothing to scroll to.
   */
  onDonutClick?: () => void;
}

function resolveLabelColorOverride(
  labelColorOverride: string | { light: string; dark: string } | undefined,
  isDark: boolean,
): string | undefined {
  if (typeof labelColorOverride === 'string') return labelColorOverride;
  if (!labelColorOverride) return undefined;
  return isDark ? labelColorOverride.dark : labelColorOverride.light;
}

interface MatchDonutPopoverProps {
  trigger: React.ReactElement;
  title: React.ReactNode;
  children: React.ReactNode;
}

function MatchDonutPopover({ trigger, title, children }: MatchDonutPopoverProps): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverCloseTimer = useCallback(() => {
    if (hoverCloseTimer.current !== null) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  useEffect(() => () => clearHoverCloseTimer(), [clearHoverCloseTimer]);

  const prefersFineHover = (): boolean =>
    typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const onMouseEnter = () => {
    if (!prefersFineHover()) return;
    clearHoverCloseTimer();
    setOpen(true);
  };

  const onMouseLeave = () => {
    if (!prefersFineHover()) return;
    clearHoverCloseTimer();
    hoverCloseTimer.current = setTimeout(() => {
      setOpen(false);
      hoverCloseTimer.current = null;
    }, 220);
  };

  return (
    <>
      {React.cloneElement(trigger, {
        ref: (node: HTMLElement | null) => setAnchorEl(node),
        onClick: () => setOpen((prev) => !prev),
        onMouseEnter,
        onMouseLeave,
      })}
      <ChartPopoverPortal
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        title={title}
        showBackdrop={!prefersFineHover()}
        width={320}
        onPopoverMouseEnter={onMouseEnter}
        onPopoverMouseLeave={onMouseLeave}
      >
        {children}
      </ChartPopoverPortal>
    </>
  );
}

/** Renders donut without react-intl; used when strings are provided (static SRP). Handles null score as empty donut with "?". */
function MatchDonutWithStrings({
  score,
  size = 64,
  className = '',
  breakdown,
  strings,
  ringColorOverride,
  labelColorOverride,
}: MatchDonutProps & { strings: MatchDonutStaticStrings }): React.ReactElement {
  const isDark = useDocumentDarkClass();
  const strokeWidth = Math.max(4, size / 12);
  const radius = (size - strokeWidth) / 2;

  if (score === null || score === undefined || !Number.isFinite(score)) {
    const ariaLabel = strings.loginPromptAria ?? 'Log in to see your match score';
    return (
      <div
        className={`relative inline-flex items-center justify-center flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
        role="img"
        aria-label={ariaLabel}
      >
        <span
          className="icon-mask transform -rotate-90 text-jc-border dark:text-jc-contrast-subtle flex-shrink-0"
          style={{
            width: size,
            height: size,
            ['--icon-url' as string]: 'url(/icons/match-donut-empty.svg)',
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 grid place-items-center"
          style={{ fontSize: `${size * 0.25}px` }}
        >
          <span className="font-bold leading-none text-jc-muted">?</span>
        </div>
        {/* Lock icon to match MatchDonutWithLoginPrompt empty state */}
        <div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-jc-cta flex items-center justify-center shadow-sm dark:shadow-none"
          style={{ fontSize: '8px' }}
        >
          <span className="text-white">🔒</span>
        </div>
      </div>
    );
  }

  const clampedScore = Math.max(0, Math.min(100, score));
  const circumference = 2 * Math.PI * radius;
  const percentage = clampedScore / 100;
  const dashOffset = circumference - percentage * circumference;
  const color = ringColorOverride ?? getMatchDonutScoreColor(clampedScore, isDark ? 'dark' : 'light');
  const labelColor = resolveLabelColorOverride(labelColorOverride, isDark) ?? color;
  const ariaLabel = strings.ariaLabelTemplate.replace('{score}', String(clampedScore));

  const breakdownTooltipBody = breakdown ? (
    <div className="grid grid-cols-2 gap-1.5 text-xs">
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">{strings.breakdownSalary}</div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink"><span className="font-semibold">{breakdown.salary}%</span> <span className="text-jc-muted">({Math.round(breakdown.weights.salaryWeight * 100)}%)</span></div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">{strings.breakdownVacation}</div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink"><span className="font-semibold">{breakdown.vacation}%</span> <span className="text-jc-muted">({Math.round(breakdown.weights.vacationWeight * 100)}%)</span></div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">{strings.breakdownBenefits}</div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink"><span className="font-semibold">{breakdown.benefits}%</span> <span className="text-jc-muted">({Math.round(breakdown.weights.benefitsWeight * 100)}%)</span></div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">{strings.breakdownRemoteness}</div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink"><span className="font-semibold">{breakdown.remoteness}%</span> <span className="text-jc-muted">({Math.round(breakdown.weights.remotenessWeight * 100)}%)</span></div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">{strings.breakdownLanguages}</div>
      <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink"><span className="font-semibold">{breakdown.languages}%</span> <span className="text-jc-muted">({Math.round(breakdown.weights.languagesWeight * 100)}%)</span></div>
    </div>
  ) : undefined;

  const donutInteractiveClass = breakdownTooltipBody
    ? 'cursor-pointer transition-transform duration-150 hover:scale-105 active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100'
    : '';

  const donutElement = (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 ${donutInteractiveClass} ${className}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="transform -rotate-90" aria-label={ariaLabel}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-jc-border dark:text-jc-elevated"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ fontSize: `${size * 0.25}px` }}
      >
        <span className="font-bold" style={{ color: labelColor }}>
          {mapMatchScoreToTier(clampedScore)}
        </span>
      </div>
    </div>
  );

  if (breakdownTooltipBody) {
    return (
      <MatchDonutPopover trigger={donutElement} title={strings.breakdownTitle}>
        {breakdownTooltipBody}
      </MatchDonutPopover>
    );
  }
  return donutElement;
}

function SearchInferenceBulletLine({ bullet }: { bullet: SearchInferenceBullet }): React.ReactElement {
  switch (bullet.type) {
    case 'keywords':
      return (
        <Msg
          id="matchDonut.searchInference.bullet.keywords"
          defaultMessage='Your searches included terms like "{terms}"; we compared them to this job’s title and role.'
          description="Explains keyword-based match from recent searches"
          values={{ terms: bullet.terms }}
        />
      );
    case 'locations':
      return (
        <Msg
          id="matchDonut.searchInference.bullet.locations"
          defaultMessage='You’ve searched in areas such as {locations}; this listing’s location is compared to that.'
          description="Explains location-based match from recent searches"
          values={{ locations: bullet.locations }}
        />
      );
    case 'remoteness':
      return (
        <Msg
          id="matchDonut.searchInference.bullet.remoteness"
          defaultMessage='You often use remoteness filters ({values}); this job is scored against those preferences.'
          description="Explains remoteness match from recent searches"
          values={{ values: bullet.values }}
        />
      );
    case 'roles':
      return (
        <Msg
          id="matchDonut.searchInference.bullet.roles"
          defaultMessage='Your searches include role filters such as {hints}; similar signals are compared to this job.'
          description="Explains role facet match from recent searches"
          values={{ hints: bullet.hints }}
        />
      );
    case 'company':
      return (
        <Msg
          id="matchDonut.searchInference.bullet.company"
          defaultMessage='You’ve shown interest in employers like {company}; we factor that into the score.'
          description="Explains company facet match from recent searches"
          values={{ company: bullet.company }}
        />
      );
    default:
      return <></>;
  }
}

/** Renders donut using react-intl (interactive tree). Call only when score is a number. */
function MatchDonutWithIntl({
  score,
  size = 64,
  className = '',
  breakdown,
  matchScoreSource = 'profile',
  searchInferenceBullets = [],
  ringColorOverride,
  labelColorOverride,
  onDonutClick,
}: MatchDonutProps & { score: number }): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const isDark = useDocumentDarkClass();
  const clampedScore = Math.max(0, Math.min(100, score));
  const strokeWidth = Math.max(4, size / 12);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = clampedScore / 100;
  const dashOffset = circumference - percentage * circumference;
  const color = ringColorOverride ?? getMatchDonutScoreColor(clampedScore, isDark ? 'dark' : 'light');
  const labelColor = resolveLabelColorOverride(labelColorOverride, isDark) ?? color;

  const fromSearches = matchScoreSource === 'recent-searches';

  const breakdownTitle = fromSearches
    ? formatMessage({
        id: 'matchDonut.searchInference.title',
        defaultMessage: 'Match from your recent searches',
        description: 'Title when match score is inferred from search history',
      })
    : formatMessage({
        id: 'matchDonut.breakdown.title',
        defaultMessage: 'Match Score Breakdown',
        description: 'Title for match score breakdown tooltip',
      });

  const breakdownTooltipBody = breakdown ? (
    <>
      {fromSearches && (
        <div className="mb-3 space-y-2">
          <p className="text-xs text-jc-muted leading-relaxed">
            <Msg
              id="matchDonut.searchInference.intro"
              defaultMessage="We don’t have your profile yet, so this score estimates fit using filters and keywords from searches you’ve run on JobCrawls (recent activity counts more)."
              description="Intro text for search-inferred match tooltip"
            />
          </p>
          {searchInferenceBullets.length > 0 ? (
            <ul className="list-disc pl-4 space-y-1.5 text-xs text-jc-ink-secondary leading-relaxed">
              {searchInferenceBullets.map((b, i) => (
                <li key={i}>
                  <SearchInferenceBulletLine bullet={b} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">
          {fromSearches ? (
            <Msg id="matchDonut.searchBreakdown.keywords" defaultMessage="Search keywords" description="Row label for search-inferred breakdown" />
          ) : (
            <Msg id="matchDonut.breakdown.salary" defaultMessage="Salary" description="Salary label in breakdown" />
          )}
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink">
          <span className="font-semibold">{breakdown.salary}%</span>{' '}
          <span className="text-jc-muted">({Math.round(breakdown.weights.salaryWeight * 100)}%)</span>
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">
          {fromSearches ? (
            <Msg id="matchDonut.searchBreakdown.location" defaultMessage="Location" description="Row label for search-inferred breakdown" />
          ) : (
            <Msg id="matchDonut.breakdown.vacation" defaultMessage="Vacation" description="Vacation label in breakdown" />
          )}
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink">
          <span className="font-semibold">{breakdown.vacation}%</span>{' '}
          <span className="text-jc-muted">({Math.round(breakdown.weights.vacationWeight * 100)}%)</span>
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">
          {fromSearches ? (
            <Msg id="matchDonut.searchBreakdown.roleFit" defaultMessage="Role & field" description="Row label for search-inferred breakdown" />
          ) : (
            <Msg id="matchDonut.breakdown.benefits" defaultMessage="Benefits" description="Benefits label in breakdown" />
          )}
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink">
          <span className="font-semibold">{breakdown.benefits}%</span>{' '}
          <span className="text-jc-muted">({Math.round(breakdown.weights.benefitsWeight * 100)}%)</span>
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">
          {fromSearches ? (
            <Msg id="matchDonut.searchBreakdown.remoteness" defaultMessage="Remoteness" description="Row label for search-inferred breakdown" />
          ) : (
            <Msg id="matchDonut.breakdown.remoteness" defaultMessage="Remoteness" description="Remoteness label in breakdown" />
          )}
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink">
          <span className="font-semibold">{breakdown.remoteness}%</span>{' '}
          <span className="text-jc-muted">({Math.round(breakdown.weights.remotenessWeight * 100)}%)</span>
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-jc-ink-secondary">
          {fromSearches ? (
            <Msg id="matchDonut.searchBreakdown.employer" defaultMessage="Employer & industry" description="Row label for search-inferred breakdown" />
          ) : (
            <Msg id="matchDonut.breakdown.languages" defaultMessage="Languages" description="Languages label in breakdown" />
          )}
        </div>
        <div className="rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1 text-right text-jc-ink">
          <span className="font-semibold">{breakdown.languages}%</span>{' '}
          <span className="text-jc-muted">({Math.round(breakdown.weights.languagesWeight * 100)}%)</span>
        </div>
      </div>
    </>
  ) : undefined;

  const donutInteractiveClass = breakdownTooltipBody || onDonutClick
    ? 'cursor-pointer transition-transform duration-150 hover:scale-105 active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100'
    : '';

  const donutElement = (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 ${donutInteractiveClass} ${className}`}
      style={{ width: size, height: size }}
      {...(onDonutClick ? { role: 'button' as const, tabIndex: 0, onClick: onDonutClick } : {})}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        aria-label={formatMessage(
          fromSearches
            ? {
                id: 'matchDonut.ariaLabelSearchInferred',
                defaultMessage: 'Estimated match from your recent searches: {score}%',
                description: 'Accessibility label when match is inferred from search history',
              }
            : {
                id: 'matchDonut.ariaLabel',
                defaultMessage: 'Match score: {score}%',
                description: 'Accessibility label for match donut chart',
              },
          { score: clampedScore }
        )}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-jc-border dark:text-jc-elevated"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" style={{ fontSize: `${size * 0.25}px` }}>
        <span className="font-bold" style={{ color: labelColor }}>
          {mapMatchScoreToTier(clampedScore)}
        </span>
      </div>
    </div>
  );

  if (breakdownTooltipBody && !onDonutClick) {
    return (
      <MatchDonutPopover trigger={donutElement} title={breakdownTitle}>
        {breakdownTooltipBody}
      </MatchDonutPopover>
    );
  }
  return donutElement;
}

/**
 * Match Donut Component
 * Shows a circular donut chart with a score from 0-100 in the center.
 * When strings are provided (static SRP), uses them and can show empty donut with "?" when score is null.
 */
export const MatchDonut: React.FC<MatchDonutProps> = (props) => {
  if (props.strings) {
    return <MatchDonutWithStrings {...props} strings={props.strings} />;
  }
  if (props.score == null || !Number.isFinite(props.score)) {
    return null;
  }
  return (
    <MatchDonutWithIntl
      {...props}
      score={props.score}
      matchScoreSource={props.matchScoreSource}
      searchInferenceBullets={props.searchInferenceBullets}
    />
  );
};

interface MatchDonutWithLoginPromptProps {
  score: number | null; // null if user not logged in or score unavailable
  size?: number;
  className?: string;
  onLoginClick?: () => void;
  breakdown?: MatchScoreBreakdown;
  /** When true and score is null, CTA links to profile instead of sign-in / create account. */
  isAuthenticated?: boolean;
  matchScoreMeta?: { source: 'recent-searches'; bullets: SearchInferenceBullet[] } | null;
  /** V2 redesign: ring stroke uses the company's brand panel color instead of the score-based color. */
  ringColorOverride?: string;
  /**
   * V2 redesign: center score digit uses a fixed ink color instead of following the ring color.
   * A plain string is used as-is regardless of theme (rare — only for a color known to work on
   * both). The donut sits on the page's own background (light/dark), not necessarily the ring
   * color's panel, so a fixed ink color needs a `{ light, dark }` pair to stay readable in both.
   */
  labelColorOverride?: string | { light: string; dark: string };
  /** See MatchDonutProps.onDonutClick; has no effect while score is null (login-prompt state). */
  onDonutClick?: () => void;
}

/**
 * Match Donut with Login Prompt
 * Shows the match donut if user is logged in, otherwise shows an empty donut with "?" in the center
 * that opens a tooltip (hover/click) with a create-account CTA.
 */
export const MatchDonutWithLoginPrompt: React.FC<MatchDonutWithLoginPromptProps> = ({
  score,
  size = 64,
  className = '',
  onLoginClick,
  breakdown,
  isAuthenticated = false,
  matchScoreMeta = null,
  ringColorOverride,
  labelColorOverride,
  onDonutClick,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();

  const profileHref = buildLocalizedProfileHref(intl.locale);

  if (score === null) {
    const emptyTooltipBody = isAuthenticated ? (
      <div className="space-y-2">
        <div className="text-xs text-jc-muted rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1.5">
          <Msg
            id="matchDonut.profilePrompt.description"
            defaultMessage="Add your current job details and preferences on your profile to see how well jobs match."
            description="Description when logged in but match score is not available"
          />
        </div>
        <Link
          href={profileHref}
          className="inline-flex w-full items-center justify-center rounded-md bg-jc-cta px-3 py-2 text-xs font-semibold text-white transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-cta/50"
        >
          <Msg
            id="matchDonut.profilePrompt.goToProfile"
            defaultMessage="Go to profile"
            description="Link to profile from match donut when logged in without a score"
          />
        </Link>
      </div>
    ) : (
      <div className="space-y-2">
        <div className="text-xs text-jc-muted rounded-md border border-jc-border/70 bg-jc-page/80 px-2 py-1.5">
          <Msg
            id="matchDonut.loginPrompt.description"
            defaultMessage="Create a profile and add your current job details to see how well jobs match your preferences"
            description="Description for login prompt tooltip"
          />
        </div>
        <button
          type="button"
          onClick={onLoginClick}
          className="inline-flex w-full items-center justify-center rounded-md bg-jc-cta px-3 py-2 text-xs font-semibold text-white transition-colors cursor-pointer hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-cta/50"
        >
          <Msg
            id="matchDonut.loginPrompt.createAccount"
            defaultMessage="Create account"
            description="Create account button in match donut login tooltip"
          />
        </button>
      </div>
    );

    const emptyAria = isAuthenticated
      ? formatMessage({
          id: 'matchDonut.profilePromptAria',
          defaultMessage: 'Complete your profile to see your match score',
          description: 'Accessibility label when logged in but match score unavailable',
        })
      : formatMessage({
          id: 'matchDonut.loginPromptAria',
          defaultMessage: 'Log in to see your match score',
          description: 'Accessibility label for login prompt',
        });

    const emptyTitle = isAuthenticated
      ? formatMessage({
          id: 'matchDonut.profilePrompt.title',
          defaultMessage: 'Complete your profile to see your match score',
          description: 'Title when logged in but match score is not available',
        })
      : formatMessage({
          id: 'matchDonut.loginPrompt.title',
          defaultMessage: 'Login to see your match score',
          description: 'Title for login prompt tooltip',
        });

    const loginButton = (
      <button
        className={`relative inline-flex items-center justify-center flex-shrink-0 cursor-pointer transition-all hover:scale-105 active:scale-95 ${className}`}
        style={{ width: size, height: size }}
        aria-label={emptyAria}
      >
        <span
          className={`icon-mask transform -rotate-90 transition-colors flex-shrink-0 ${ringColorOverride ? '' : 'text-jc-border hover:text-jc-contrast-subtle dark:text-jc-contrast-subtle dark:hover:text-jc-muted'}`}
          style={{
            width: size,
            height: size,
            color: ringColorOverride,
            ['--icon-url' as string]: 'url(/icons/match-donut-empty.svg)',
          }}
          aria-hidden
        />
        {/* Question mark in center with lock icon indicator; grid + leading-none for optical centering in donut */}
        <div
          className="absolute inset-0 grid place-items-center"
          style={{ fontSize: `${size * 0.25}px` }}
        >
          <span className="font-bold leading-none text-jc-muted">
            ?
          </span>
        </div>
        {/* Small lock icon in corner to indicate login required */}
        <div
          className={`absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center shadow-sm dark:shadow-none ${ringColorOverride ? '' : 'bg-jc-cta'}`}
          style={{ fontSize: '8px', background: ringColorOverride }}
        >
          <span className="text-white">🔒</span>
        </div>
      </button>
    );
    
    return (
      <MatchDonutPopover trigger={loginButton} title={emptyTitle}>
        {emptyTooltipBody}
      </MatchDonutPopover>
    );
  }

  return (
    <MatchDonut
      score={score}
      size={size}
      className={className}
      breakdown={breakdown}
      matchScoreSource={matchScoreMeta?.source === 'recent-searches' ? 'recent-searches' : 'profile'}
      searchInferenceBullets={matchScoreMeta?.bullets}
      ringColorOverride={ringColorOverride}
      labelColorOverride={labelColorOverride}
      onDonutClick={onDonutClick}
    />
  );
};
